import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  getMantisBusinesses,
  createMantisBusiness,
  updateMantisBusiness,
  updateMainUserPassword,
} from "../ApiCalls/backoffice";
import { Spinner, Row, Col, Table, Card, CardBody, Input, Button, Alert } from "reactstrap";
import BusinessFormModal from "./components/BusinessFormModal";
import PageHeader from "./components/PageHeader";

const select = (state) => {
  return {
    userId: state.auth.userId,
    token: state.auth.token,
  };
};

const MantisBusinesses = (props) => {
  const [mantisBusinesses, setMantisBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const isSysdatec = props.userId === "6663a156c03068303336ab1b";
  const sysdatecBusinessesIds = [
    "6663c56af8106c9a9c3a5a0c",
    "6663c42d8217602d15abe2fe",
    "6663c3008217602d15abe2e2",
  ];

  useEffect(() => {
    fetchMantisBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; fetchMantisBusinesses is redefined each render and would cause a refetch loop
  }, []);

  // Filter businesses based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredBusinesses(mantisBusinesses);
    } else {
      const filtered = mantisBusinesses.filter((business) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          business.name?.toLowerCase().includes(searchLower) ||
          business.contactEmail?.toLowerCase().includes(searchLower) ||
          business.identificationNumber?.toLowerCase().includes(searchLower) ||
          business.identificationType?.toLowerCase().includes(searchLower) ||
          `${business.contactFirstName} ${business.contactLastName}`
            .toLowerCase()
            .includes(searchLower)
        );
      });
      setFilteredBusinesses(filtered);
    }
  }, [searchTerm, mantisBusinesses]);

  // Fetch Mantis businesses from API
  const fetchMantisBusinesses = async () => {
    try {
      const response = await getMantisBusinesses(props.token, props.userId);
      if (response.success) {
        setMantisBusinesses(response.mantisBusinesses);
        setFilteredBusinesses(response.mantisBusinesses);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching Mantis businesses:", error);
    }
    setLoading(false);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setSelectedBusiness(null);
    }
  };

  const handleCreateClick = () => {
    setSelectedBusiness(null);
    setModalOpen(true);
  };

  const handleEditClick = (business) => {
    setSelectedBusiness(business);
    setModalOpen(true);
  };

  const handleFormSubmit = async (businessData, mainUserPassword) => {
    setSubmitting(true);
    try {
      const res = selectedBusiness
        ? await updateMantisBusiness(
            props.userId,
            selectedBusiness._id,
            businessData,
            props.token
          )
        : await createMantisBusiness(
            props.userId,
            businessData,
            mainUserPassword,
            props.token
          );

      if (res.success) {
        let successMessage = selectedBusiness
          ? "Empresa actualizada correctamente"
          : "Empresa creada correctamente";

        // On edit the password field is optional and resets the main account login.
        if (selectedBusiness && mainUserPassword) {
          const passwordRes = await updateMainUserPassword(
            props.userId,
            selectedBusiness._id,
            mainUserPassword,
            props.token
          );
          successMessage = passwordRes.success
            ? "Empresa y contraseña actualizadas correctamente"
            : `Error: empresa actualizada, pero la contraseña no se pudo cambiar (${
                passwordRes.message || "error desconocido"
              })`;
        }

        setMessage(successMessage);
        setModalOpen(false);
        setSelectedBusiness(null);
        fetchMantisBusinesses();
      } else {
        setMessage(
          `Error: ${res.message || "No se pudo guardar la empresa"}`
        );
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mantis-businesses-page">
      <PageHeader
        title="Empresas"
        subtitle="Gestión y administración de empresas"
      />

      {/* Main Content */}
      <div className="page-content">
        {message && (
          <Alert
            color={message.includes("Error") ? "danger" : "success"}
            isOpen={message !== ""}
            toggle={() => setMessage("")}
            className="mb-3"
          >
            {message}
          </Alert>
        )}

        {loading ? (
          <div className="loading-container">
            <Spinner color="primary" />
            <p>Cargando empresas...</p>
          </div>
        ) : (
          <>
            {/* Search Section */}
            <Row className="mb-4">
              <Col xs={12}>
                <Card className="search-card">
                  <CardBody>
                    <div className="search-container">
                      <div className="search-input-wrapper">
                        <i className="fas fa-search search-icon" />
                        <Input
                          type="text"
                          placeholder="Buscar empresas por nombre, email, NIT o contacto..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          className="search-input"
                        />
                        {searchTerm && (
                          <button
                            className="clear-search-btn"
                            onClick={clearSearch}
                            title="Limpiar búsqueda"
                          >
                            <i className="fas fa-times" />
                          </button>
                        )}
                      </div>
                      <div className="search-info" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px" }}>
                        <div>
                          <span className="results-count">
                            {filteredBusinesses.length} de{" "}
                            {mantisBusinesses.length} empresas
                          </span>
                          {searchTerm && (
                            <span className="search-term ms-3">
                              Buscando: "{searchTerm}"
                            </span>
                          )}
                        </div>
                        <Button
                          color="success"
                          size="sm"
                          onClick={handleCreateClick}
                        >
                          <i className="fas fa-plus me-2"></i>
                          Crear empresa
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Results Section */}
            <Row>
              <Col xs={12}>
                <Card className="businesses-card">
                  <CardBody>
                    {filteredBusinesses.length === 0 ? (
                      <div className="no-results">
                        <i className="fas fa-search" />
                        <h3>No se encontraron resultados</h3>
                        <p>
                          {searchTerm
                            ? `No hay empresas que coincidan con "${searchTerm}"`
                            : "No hay empresas disponibles"}
                        </p>
                        {searchTerm && (
                          <button
                            className="clear-search-button"
                            onClick={clearSearch}
                          >
                            <i className="fas fa-times" />
                            Limpiar búsqueda
                          </button>
                        )}
                      </div>
                    ) : (
                      <Table responsive className="businesses-table">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Fecha de creación</th>
                            <th>Email de contacto</th>
                            <th>Teléfono de contacto</th>
                            <th>Nombre de contacto</th>
                            <th>Documento</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBusinesses.map((mantisBusiness) => {
                            if (
                              isSysdatec &&
                              !sysdatecBusinessesIds.includes(
                                mantisBusiness._id
                              )
                            ) {
                              return null;
                            }
                            return (
                              <tr key={mantisBusiness._id}>
                                <td className="business-name">
                                  {mantisBusiness.name}
                                </td>
                                <td>
                                  {new Date(
                                    mantisBusiness.dateCreated
                                  ).toLocaleDateString("es")}
                                </td>
                                <td>{mantisBusiness.contactEmail}</td>
                                <td>{mantisBusiness.contactPhone}</td>
                                <td>
                                  {mantisBusiness.contactFirstName}{" "}
                                  {mantisBusiness.contactLastName}
                                </td>
                                <td>
                                  {mantisBusiness.identificationType}{" "}
                                  {mantisBusiness.identificationNumber}
                                </td>
                                <td className="actions-cell">
                                  <button
                                    className="view-button"
                                    onClick={() =>
                                      window.open(
                                        `/backoffice/mantisBusinesses/${mantisBusiness._id}`,
                                        "rel=noopener noreferrer"
                                      )
                                    }
                                    title="Ver detalles"
                                  >
                                    <i className="fas fa-eye" />
                                  </button>
                                  <button
                                    className="edit-button"
                                    onClick={() => handleEditClick(mantisBusiness)}
                                    title="Editar"
                                    style={{ marginLeft: "8px" }}
                                  >
                                    <i className="fas fa-edit" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>

      <BusinessFormModal
        isOpen={modalOpen}
        toggle={toggleModal}
        onSubmit={handleFormSubmit}
        isLoading={submitting}
        business={selectedBusiness}
      />
    </div>
  );
};

export default connect(select)(MantisBusinesses);
